import { useQuery, useMutation, skipToken, type UseQueryResult } from "@tanstack/react-query";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import {
  listReportsEndpoint,
  regenerateRoadsenseLatestEndpoint,
  safeDecodeRoadSenseListing,
} from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";

/**
 * Resolves the tenant's most-recent **ready** RoadSense report and whether a
 * given `siteId` appears in that report's per-site listing — the two facts the
 * Crossline Mapping config page's "View Report" button needs to build a per-site
 * deep link (`…/report/{reportId}/site/{siteId}`).
 *
 * Single read: `listReports` with `status: "ready"` (a page, not `limit: 1` —
 * see {@link LATEST_READY_PAGE_SIZE}) returns the newest ready row, and that row
 * already carries the denormalised `listingSummary` (see `ReportListItem`), so
 * we decode `sites` and test membership straight off the list item. We do NOT
 * re-`getReportById` for the same summary: that extra round-trip is redundant,
 * and — because a non-ready row 409s by-id — a race where the row flipped to
 * `generating` between the two calls would wrongly hide View despite the list
 * page already holding a usable listing.
 *
 * Kept separate from the regenerate mutation below so the button can render
 * (enabled only when a matching report resolves) without coupling to the
 * fire-and-forget write.
 */

const STALE_TIME_MS = 60_000;

/**
 * Page size for the "newest ready report" list read. The API applies `status`
 * as a DynamoDB FilterExpression AFTER `limit` bounds the rows *read*, so a
 * `limit: 1` would false-empty when the head rows aren't ready (a fresh
 * `generating` row right after a regenerate, plus the odd stuck `failed`). A
 * page comfortably larger than any realistic run of non-ready head rows makes
 * the first ready row appear on the page; kept in step with the backend
 * `getLatestReadyReport` resolver.
 */
const LATEST_READY_PAGE_SIZE = 25;

export interface LatestRoadSenseReportForSite {
  /** `reportId` of the newest ready report, or `undefined` when none exists. */
  readonly reportId: string | undefined;
  /** True when the resolved report's listing includes the configured `siteId`. */
  readonly siteInReport: boolean;
}

export interface UseLatestRoadSenseReportForSiteResult {
  readonly reportId: string | undefined;
  readonly siteInReport: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isTenantPending: boolean;
}

export function useLatestRoadSenseReportForSite(siteId: string | undefined): UseLatestRoadSenseReportForSiteResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();

  const enabled = !!apiConfig.tenant && !!siteId;

  const query = useQuery({
    queryKey: ["roadsense", "latest-report-for-site", apiConfig.tenant?.tenantId, siteId] as const,
    queryFn: apiConfig.tenant && siteId ? ({ signal }) => resolveLatestForSite(apiConfig, siteId, signal) : skipToken,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  }) as UseQueryResult<LatestRoadSenseReportForSite, Error>;

  return {
    reportId: query.data?.reportId,
    siteInReport: query.data?.siteInReport ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    isTenantPending: isTenantLoading || !tenantId,
  };
}

async function resolveLatestForSite(
  apiConfig: ApiConfig,
  siteId: string,
  signal: AbortSignal | undefined,
): Promise<LatestRoadSenseReportForSite> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    throw new Error("Tenant not configured");
  }

  // Step 1: newest READY report. NB: the API's `status` is a DynamoDB
  // FilterExpression applied AFTER `limit` bounds the rows *read*, so
  // `limit: 1` false-empties whenever the newest row isn't ready (e.g. a fresh
  // `generating` row right after a regenerate). Request a page instead and take
  // the first ready row (results are newest-first, ready-only), matching the
  // backend `getLatestReadyReport` resolver's first-page sizing.
  const listUrl =
    tenant.apiBaseUrl +
    expandEndpoint(listReportsEndpoint, {
      path: { tenantId: tenant.tenantId, type: "roadsense" },
      query: { status: "ready", limit: LATEST_READY_PAGE_SIZE },
    });
  const listResponse = await authenticatedFetch(listUrl, signal ? { signal } : {});
  if (!listResponse.ok) {
    const text = await listResponse.text().catch(() => "");
    throw new Error(text || `Request failed: ${String(listResponse.status)}`);
  }
  const listBody: unknown = await listResponse.json();
  const latest = listReportsEndpoint.responseSchema.parse(listBody).data[0];
  if (!latest) {
    return { reportId: undefined, siteInReport: false };
  }

  // The list row already carries the denormalised `listingSummary`, so decode
  // site membership straight off it — no second by-id fetch (which would be
  // redundant, and 409s on a row that raced to `generating`, wrongly hiding
  // View).
  const siteInReport = siteIsInListing(latest.listingSummary, siteId);
  return { reportId: latest.reportId, siteInReport };
}

/**
 * Pure decision for "does this report's per-site listing include `siteId`?".
 *
 * Extracted so it can be unit-tested without a live query: the listing is a
 * `safeDecode` (a report row can legitimately carry an unparseable/absent
 * `listingSummary`), and a decode miss must resolve to `false` (hide the deep
 * link) rather than throw and break the config page.
 */
export function siteIsInListing(listingSummary: unknown, siteId: string): boolean {
  const summary = safeDecodeRoadSenseListing(listingSummary);
  return summary.success && summary.data.sites.some((s) => s.siteId === siteId);
}

export interface UseRegenerateRoadSenseLatestResult {
  /** Fire-and-forget trigger; rejects on non-2xx so callers can toast the error. */
  readonly regenerate: () => Promise<void>;
  readonly isPending: boolean;
}

/**
 * Fire-and-forget mutation that re-enumerates + regenerates the tenant's
 * most-recent RoadSense report from live `ScenarioStatus` + subscriptions
 * (`POST …/roadsense/regenerate-latest`, empty body). We do not poll or read the
 * echoed identity back — the config page only shows a "regenerating…" toast.
 */
export function useRegenerateRoadSenseLatest(): UseRegenerateRoadSenseLatestResult {
  const apiConfig = useApiConfig();

  const mutation = useMutation<void, Error, void>({
    mutationFn: async () => {
      const { tenant, authenticatedFetch } = apiConfig;
      if (!tenant) {
        throw new Error("Tenant not configured");
      }
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(regenerateRoadsenseLatestEndpoint, {
          path: { tenantId: tenant.tenantId, type: "roadsense" },
        });
      // POST with an (intentionally empty) JSON body — the shared `defineEndpoint`
      // POST overload always registers a `bodySchema`, so `{}` satisfies it.
      const response = await authenticatedFetch(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Request failed: ${String(response.status)}`);
      }
      // Validate but discard — fire-and-forget: no polling on the echoed identity.
      regenerateRoadsenseLatestEndpoint.responseSchema.parse(await response.json());
    },
  });

  return {
    regenerate: () => mutation.mutateAsync(),
    isPending: mutation.isPending,
  };
}
